"use client";
// Helper to build GitHub public folder URL

import { motion, useScroll, useTransform } from "framer-motion";
import { useMemo, useRef, useState, useEffect } from "react";


// Placeholder CounterAnimation component
const CounterAnimation = ({ target, suffix, label, duration }) => {
  return (
    <div className="text-center">
      <div className="text-4xl md:text-5xl font-bold text-yellow-500">
        {target}
        {suffix}
      </div>
      <div className="text-lg mt-2">{label}</div>
    </div>
  );
};

// Convert normal Arabic to dotless for display only (keeps connections)
const convertToDotlessArabic = (text) => {
  if (!text) return text;

  const dotlessMap = {
    ب: "ٮ",
    ت: "ٮ",
    ث: "ٮ",
    ج: "ح",
    خ: "ح",
    ذ: "د",
    ز: "ر",
    ش: "س",
    ض: "ص",
    ظ: "ط",
    غ: "ع",
    ف: "ڡ",
    ق: "ٯ",
    ن: "ں",
    ي: "ى",
    ئ: "ى",
    ؤ: "و",
    أ: "ا",
    إ: "ا",
  };

  return text
    .split("")
    .map((char) => dotlessMap[char] || char)
    .join("");
};

const HeroSection = ({ data }) => {
  // Fallback to hardcoded values if data i not available
  const title = data?.title || "نخلة بابل الرائدة في مجال الدعاية والإعلان في العراق";
  const subtitle = data?.subtitle || "كن مختلف باعلانك";
  const description =
    data?.description ||
    "تعتبر نخلة بابل الشركة الرائدة في توفير حلول الإعلانات الخارجية والداخلية منذ عام 2012 ، حيث توفر لعملائها خوض غمار التجربة الإعلانية من خلال العديد من المنصات التي توفرها، حيث تمتلك نخلة بابل الحق الإعلاني الحصري في اهم المولات التجارية في العراق بالإضافة الى أكبر شبكة لوحات وشاشات إعلانية في العراق.";
    const videoUrl = data?.video?.url || data?.videoUrl || "https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/hero.mp4"; // Ensure R2 URL
  const counterValue = data?.counterValue || 1500;
  const counterSuffix = data?.counterSuffix || "+";
  const counterLabel = data?.counterLabel || "حملة اعلانية ناجحة";
  const useSubtitleGif = data?.useSubtitleGif !== false;
    const subtitleGifUrl = data?.subtitleGif?.url || "https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/Kn.gif"; // Ensure R2 URL

  // Use subtitle directly (already converted to dotless)
  const displaySubtitle = subtitle;

  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [isWhatsAppOpen, setIsWhatsAppOpen] = useState(false);
  // GIF src with cache-buster to force refresh from source on client mount
  const [gifSrc, setGifSrc] = useState("/Kn.gif");

  useEffect(() => {
    // Append a timestamp query param once on mount to bust caches
    setGifSrc(`/Kn.gif?v=${Date.now()}`);
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0.2]);

  const handleWhatsAppClick = () => {
    // Replace with your actual WhatsApp number
    const phoneNumber = "9647801234567"; // Format: country code + number (no + or spaces)
    const message = encodeURIComponent("مرحباً، أود الاستفسار عن خدماتكم");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
  };

  return (
    <>
      <motion.section
        ref={sectionRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ y: heroY, opacity: heroOpacity }}
      >
        {/* Background Video */}
        <div className="absolute inset-0 will-change-transform">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            poster={"https://pub-0705ed3bb3a5425799ba64b1bd76a81b.r2.dev/shared_media/arabic.jpg"}
            className="w-full h-full object-cover"
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/90 via-[#0a367b]/70 to-[#0a367b]/50"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1
              className="text-3xl md:text-7xl font-bold mb-8 mt-10 leading-tight"
              dir="rtl"
              lang="ar"
            >
              <br />
              {title}
            </h1>

            {/* Subtitle - Either GIF or Text */}
            {useSubtitleGif ? (
              subtitle && (
                <div className="mb-8 flex justify-center">
                  <img
                    src={subtitleGifUrl}
                    alt={subtitle}
                    width={400}
                    height={300}
                    className="rounded-lg shadow-lg"
                    loading="eager"
                  />
                </div>
              )
            ) : (
              subtitle && (
                <h2 className="text-2xl md:text-4xl font-bold mb-8 text-[#dfa428]">
                  {displaySubtitle}
                </h2>
              )
            )}

            {description && (
              <p className="text-xl md:text-2xl mb-12 leading-relaxed opacity-90">
                {description}
              </p>
            )}

            <div className="flex justify-center">
              <CounterAnimation
                target={counterValue}
                suffix={counterSuffix}
                label={counterLabel}
                duration={3}
              />
            </div>
          </div>
        </div>
      </motion.section>

      {/* Fixed Sticky WhatsApp Button - Bottom Right */}
    </>
  );
};

export default HeroSection;
